import React, { FC } from 'react';
import { MdAdminPanelSettings, MdSupervisorAccount, MdTune } from 'react-icons/md';
import UiUserMenuDialog, {
    AppearanceSection,
    useUserMenuAccounts,
    type IUserMenuSection,
} from '@srm/ui/shell/UserMenu';
import { IListElement } from '@srm/ui/types/IListElement';
import { useUi } from '@srm/ui/provider';
import { toRouterPath } from '@srm/ui/lib/navigationGuard';
import UserPanel from '@molecules/UserPanel/UserPanel';
import VersionPanel from '@molecules/VersionPanel/VersionPanel';
import SubstitutionBanner from '@molecules/SubstitutionBanner/SubstitutionBanner';
import ReplacingSummary from '@molecules/ReplacingSummary/ReplacingSummary';
import { IDebugSettings } from '@models/IDebugSettings';
import { accountsUi, useMultiAccount } from '@/auth/multiAccount';
import PersonalSettingsScreen from './parts/PersonalSettingsScreen';

export interface IUserMenuDialogProps {
    /** Кнопка-якорь: элемент, принимающий ref. */
    target: React.ReactNode;
    opened: boolean;
    /** Маршруты раздела «Личные настройки» (настройки, уведомления, custom-формы). */
    personalItems: IListElement[];
    /** Пункты «Управления доступом»: единственный — в корне, пустой раздел скрыт. */
    accessItems: IListElement[];
    /** Пункты «Замещений»: единственный — в корне, пустой раздел скрыт. */
    deputyItems: IListElement[];
    /** «Выход из профиля»; отсутствует, если нет права signOut. */
    onLogout?: () => void;
    /** Отладочные настройки — передаются только вне PROD. */
    debugSettings?: IDebugSettings;
    onChangeDebugSettings?: (value: IDebugSettings) => void;
    onClose: () => void;
    onOrgChange: () => void;
    onEditAvatar: () => void;
    onDismissHint: () => void;
    avatarHintOpen: boolean;
    onDismissThemeHint: () => void;
    themeHintOpen: boolean;
}

/**
 * Меню профиля Front — `UserMenuDialog` из `@srm/ui/shell/UserMenu` с разделами приложения: карточка из стора,
 * замещения, учётные записи браузера (мультиаккаунт), «Личные настройки» (таблицы, документы, отладка), «Управление
 * доступом» и «Замещения».
 */
const UserMenuDialog: FC<IUserMenuDialogProps> = ({
    target,
    opened,
    personalItems,
    accessItems,
    deputyItems,
    onLogout,
    debugSettings,
    onChangeDebugSettings,
    onClose,
    onOrgChange,
    onEditAvatar,
    onDismissHint,
    avatarHintOpen,
    onDismissThemeHint,
    themeHintOpen,
}) => {
    const multiAccount = useMultiAccount();
    const { basename = '' } = useUi();
    // Выше выпадающей панели: закрытие меню не теряет ход переключения и отказ
    const menuAccounts = useUserMenuAccounts({
        ...accountsUi,
        ...multiAccount,
        opened,
        // адрес — в момент нажатия: подписка на роутер перерисовывала бы меню на каждом переходе
        addAccountUrl: () => accountsUi.addAccountUrl(toRouterPath(window.location.href, basename)),
        onLogout,
    });

    const sections: IUserMenuSection[] = [
        {
            id: 'personal',
            title: 'Личные настройки',
            hint: 'Таблицы, настройки, уведомления',
            icon: <MdTune size="24" />,
            content: (
                <PersonalSettingsScreen
                    items={personalItems}
                    debugSettings={debugSettings}
                    onChangeDebugSettings={onChangeDebugSettings}
                />
            ),
        },
        {
            id: 'access',
            title: 'Управление доступом',
            hint: 'Активация пользователей, роли',
            icon: <MdAdminPanelSettings size="24" />,
            items: accessItems,
        },
        {
            id: 'deputy',
            title: 'Замещения',
            hint: 'Заместители и переадресация уведомлений',
            icon: <MdSupervisorAccount size="24" />,
            items: deputyItems,
        },
    ];

    return (
        <UiUserMenuDialog
            target={target}
            opened={opened}
            onClose={onClose}
            profile={
                <UserPanel
                    onOrgChange={onOrgChange}
                    onEditAvatar={onEditAvatar}
                    onDismissHint={onDismissHint}
                    avatarHintOpen={avatarHintOpen}
                />
            }
            profileExtra={
                <>
                    <SubstitutionBanner onClose={onClose} />
                    <ReplacingSummary />
                </>
            }
            accounts={menuAccounts.accounts}
            appearance={<AppearanceSection hintOpen={themeHintOpen} onDismissHint={onDismissThemeHint} />}
            sections={sections}
            onLogout={onLogout}
            onLogoutAll={menuAccounts.onLogoutAll}
            footer={<VersionPanel />}
        />
    );
};

export default UserMenuDialog;
